import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import useGlobal from '../../../store';
import { getLookPreset } from '../../../settings/render';
import { getGfxQuality } from '../../../settings/gfx.quality';

/**
 * Soft PCSS shadows — patches three.js shader chunks once.
 * Skipped entirely on mobile / low tiers (fill-rate killer).
 */
export function SoftShadowsInit() {

  useEffect(() => {

    let cancelled = false;
    const gfx = getGfxQuality();

    if ( !gfx.softShadows ) return undefined;

    const samples = gfx.softShadowSamples || 8;
    const rings = gfx.softShadowRings || 7;

    import( '@react-three/drei' ).then(( drei ) => {

      if ( cancelled || !drei.softShadows ) return;

      drei.softShadows({

        frustum: 3.75,
        size: 0.01,
        near: 9.5,
        samples,
        rings,

      });

    });

    return () => { cancelled = true; };

  }, []);

  return null;

}

/**
 * Invisible bright mesh used as GodRays light source (outside the window).
 */
export const GodRaySun = React.forwardRef( function GodRaySun({ look: lookProp }, ref ) {

  const look = lookProp || getLookPreset();
  const sun = look.sunPosition || [ 40, 28, -90 ];
  // Place a small emissive orb far along the sun direction
  const len = Math.hypot( sun[ 0 ], sun[ 1 ], sun[ 2 ] ) || 1;
  const pos = [

    ( sun[ 0 ] / len ) * 12,
    ( sun[ 1 ] / len ) * 12,
    ( sun[ 2 ] / len ) * 12,

  ];

  return (

    <mesh ref = { ref } position = { pos } frustumCulled = { false }>

      <sphereGeometry args = {[ 0.55, 24, 24 ]} />
      <meshBasicMaterial

        color = { look.directionalColor || '#ffe7c4' }
        transparent
        opacity = { 0.95 }
        depthWrite = { false }
        toneMapped = { false }

      />

    </mesh>

  );

});

/**
 * Dust motes drifting in the sun beam — magazine CGI staple.
 */
export function SunMotes({ count = 220, look: lookProp }) {

  const points = useRef();
  const look = lookProp || getLookPreset();
  const gfx = getGfxQuality();
  const enabled = !!gfx.motes;
  const n = !enabled ? 0 : ( gfx.id === 'low' ? 60 : count );

  const { geometry, speeds } = useMemo(() => {

    const positions = new Float32Array( Math.max( n, 1 ) * 3 );
    const speeds = new Float32Array( Math.max( n, 1 ) );

    for ( let i = 0; i < n; i++ ) {

      positions[ i * 3 ] = ( Math.random() - 0.5 ) * 2.4;
      positions[ i * 3 + 1 ] = -1.2 + Math.random() * 2.6;
      positions[ i * 3 + 2 ] = ( Math.random() - 0.5 ) * 2.2;
      speeds[ i ] = 0.04 + Math.random() * 0.1;

    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute( 'position', new THREE.BufferAttribute( positions, 3 ) );

    return { geometry, speeds };

  }, [ n ]);

  useFrame(( _, dt ) => {

    if ( !enabled || !points.current || n === 0 ) return;

    const attr = points.current.geometry?.attributes?.position;
    if ( !attr?.array ) return;

    const arr = attr.array;

    for ( let i = 0; i < n; i++ ) {

      arr[ i * 3 + 1 ] += speeds[ i ] * dt;
      arr[ i * 3 ] += Math.sin( arr[ i * 3 + 1 ] * 2 + i ) * dt * 0.02;

      if ( arr[ i * 3 + 1 ] > 1.5 ) {

        arr[ i * 3 + 1 ] = -1.3;
        arr[ i * 3 ] = ( Math.random() - 0.5 ) * 2.4;
        arr[ i * 3 + 2 ] = ( Math.random() - 0.5 ) * 2.2;

      }

    }

    attr.needsUpdate = true;

  });

  if ( !enabled || n === 0 ) return null;

  return (

    <points

      ref = { points }
      geometry = { geometry }
      frustumCulled = { false }

    >

      <pointsMaterial

        size = { 0.004 }
        color = { look.directionalColor || '#fff4e0' }
        transparent
        opacity = { 0.35 }
        depthWrite = { false }
        sizeAttenuation
        blending = { THREE.AdditiveBlending }
        toneMapped = { false }

      />

    </points>

  );

}

/**
 * Short cinematic push-in on first open — then hands control back to OrbitControls.
 */
export function HeroCameraIntro({ duration = 2.4 }) {

  const { camera } = useThree();
  const start = useMemo(() => new THREE.Vector3( 0, -0.12, -0.72 ), []);
  const end = useMemo(() => new THREE.Vector3( 0, 0, -1.01 ), []);
  const done = useRef( false );
  const t0 = useRef( null );

  useEffect(() => {

    camera.position.copy( start );

  }, [ camera, start ]);

  useFrame(( state ) => {

    if ( done.current ) return;

    if ( t0.current === null ) t0.current = state.clock.elapsedTime;

    const t = Math.min( 1, ( state.clock.elapsedTime - t0.current ) / duration );
    const e = 1 - Math.pow( 1 - t, 3 ); // ease-out cubic

    camera.position.lerpVectors( start, end, e );

    if ( t >= 1 ) done.current = true;

  });

  return null;

}

/**
 * Visual LED strip under the ceiling when added in the estimate.
 */
export function LedStripVisual() {

  const [ { ledStrip } ] = useGlobal();

  if ( !ledStrip?.added ) return null;

  const length = Math.min( 4.5, Math.max( 1.2, ( +ledStrip.size || 2 ) * 0.35 ) );

  return (

    <mesh position = {[ -0.4, 1.28, 0 ]} rotation = {[ 0, 0, 0 ]}>

      <boxGeometry args = {[ length, 0.012, 0.018 ]} />
      <meshStandardMaterial

        color = "#ffffff"
        emissive = "#fff2cc"
        emissiveIntensity = { 3.2 }
        toneMapped = { false }

      />

    </mesh>

  );

}

/**
 * Collects sun mesh for GodRays and exposes via onReady callback.
 */
export function CinematicLayer({ onSunReady, look }) {

  const [ sun, setSun ] = useState( null );
  const gfx = getGfxQuality();

  useEffect(() => {

    onSunReady && onSunReady( gfx.godRays ? sun : null );

  }, [ sun, onSunReady, gfx.godRays ]);

  return (

    <group>

      { gfx.softShadows && <SoftShadowsInit /> }
      { gfx.godRays && <GodRaySun ref = { setSun } look = { look } /> }
      { gfx.motes && <SunMotes look = { look } /> }
      <HeroCameraIntro />
      <LedStripVisual />

    </group>

  );

}
